import React from 'react';
import { Clock, X, CheckCircle } from 'lucide-react';

// Modal de Agendamento de pedido (loja fechada) do cardapio publico.
// Extraido VERBATIM de PublicMenu.js (Fase 2 refactor). Componente puro (sem hooks),
// recebe estado/handlers via props. Renderizado como <SchedulingModal .../>.
const SchedulingModal = ({
  showSchedulingModal,
  selectedScheduleDate,
  selectedScheduleTime,
  schedulingSlots,
  loadingSlots,
  setIsScheduledOrder,
  setShowSchedulingModal,
  setCurrentScreen,
  setSelectedScheduleDate,
  setSelectedScheduleTime,
  setSchedulingModalShown,
}) => {
    if (!showSchedulingModal) return null;

    const handleConfirmScheduling = () => {
      if (!selectedScheduleDate || !selectedScheduleTime) {
        return;
      }
      setIsScheduledOrder(true);
      setShowSchedulingModal(false);
      // Continue to checkout - keep schedulingModalShown true to prevent re-show
      setCurrentScreen('checkout');
    };

    const handleCancelScheduling = () => {
      setSelectedScheduleDate(null);
      setSelectedScheduleTime(null);
      setIsScheduledOrder(false);
      setShowSchedulingModal(false);
      setSchedulingModalShown(false); // Reset to allow re-show if user tries again
      setCurrentScreen('home');
    };

    // Get selected slot info
    const selectedSlot = schedulingSlots.find(s => s.date === selectedScheduleDate);

    return (
      <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleCancelScheduling}
        />
        
        {/* Modal Content - Otimizado para mobile */}
        <div 
          className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl animate-slide-up flex flex-col"
          style={{ maxHeight: 'calc(100vh - 40px)', maxHeight: 'calc(100dvh - 40px)' }}
        >
          {/* Header Compacto */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-4 rounded-t-2xl sm:rounded-t-2xl flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-white">
                  Loja fechada agora
                </h3>
                <p className="text-white/90 text-sm">
                  Agende seu pedido para depois! 📅
                </p>
              </div>
              <button 
                onClick={handleCancelScheduling}
                className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-4 min-h-0">
            {loadingSlots ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : (
              <>
                {/* Date Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📅 Escolha o dia
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {schedulingSlots.slice(0, 6).map((slot) => (
                      <button
                        key={slot.date}
                        onClick={() => {
                          setSelectedScheduleDate(slot.date);
                          setSelectedScheduleTime(null);
                        }}
                        className={`p-2 rounded-lg border-2 transition-all duration-200 text-center ${
                          selectedScheduleDate === slot.date
                            ? 'border-amber-500 bg-amber-50 shadow-md'
                            : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                        }`}
                        data-testid={`schedule-date-${slot.date}`}
                      >
                        <div className="font-semibold text-sm text-gray-800">
                          {slot.is_today ? 'Hoje' : slot.is_tomorrow ? 'Amanhã' : slot.day_name?.slice(0,3)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {slot.date_formatted?.split('/').slice(0,2).join('/')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Selection */}
                {selectedScheduleDate && selectedSlot && (
                  <div className="mb-4 animate-fade-in">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ⏰ Escolha o horário
                    </label>
                    <div className="grid grid-cols-4 gap-1.5 max-h-28 overflow-y-auto">
                      {selectedSlot.time_slots.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedScheduleTime(time)}
                          className={`py-2 px-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                            selectedScheduleTime === time
                              ? 'border-amber-500 bg-amber-500 text-white shadow-md'
                              : 'border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-gray-50'
                          }`}
                          data-testid={`schedule-time-${time}`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Summary - Compacto */}
                {selectedScheduleDate && selectedScheduleTime && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 animate-fade-in">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">Agendado:</span>{' '}
                          {selectedSlot?.is_today ? 'Hoje' : selectedSlot?.is_tomorrow ? 'Amanhã' : selectedSlot?.day_name} às <span className="font-bold">{selectedScheduleTime}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer Actions - Sticky */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white rounded-b-2xl safe-area-bottom">
            <button
              onClick={handleConfirmScheduling}
              disabled={!selectedScheduleDate || !selectedScheduleTime}
              className={`w-full py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                selectedScheduleDate && selectedScheduleTime
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg hover:shadow-xl active:scale-[0.98]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="confirm-scheduling-btn"
            >
              <Clock className="w-5 h-5" />
              {selectedScheduleDate && selectedScheduleTime ? 'Confirmar e Continuar' : 'Selecione data e horário'}
            </button>
            <button
              onClick={handleCancelScheduling}
              className="w-full py-2.5 mt-2 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-100 transition-colors"
              data-testid="cancel-scheduling-btn"
            >
              Voltar ao cardápio
            </button>
          </div>
        </div>
      </div>
    );
  };

export default SchedulingModal;
